/**
 * Analytics - Entity & Sighting Events
 * 
 * @atomic - Entity and sighting tracking
 * @modular - Content interaction tracking
 * 
 * Purpose: Track entity views, sightings, confirmations
 * Exports: trackEntityView, trackSightingCreated, trackConfirmation
 */

import { trackEvent } from './core'
import { createLogger } from '../logger'

const logger = createLogger('EntityEvents')

/**
 * Helper to track first-time actions
 * @private
 */
const trackFirstTimeAction = (flagKey, category, action, label, params = {}) => {
  const localStorageKey = `analytics_first_${flagKey}`
  
  if (!localStorage.getItem(localStorageKey)) {
    trackEvent(category, action, label, null, {
      ...params,
      is_first_time: true,
      timestamp: new Date().toISOString(),
    })
    localStorage.setItem(localStorageKey, 'true')
    logger.info(`First-time ${action} tracked`, params)
  }
}

/**
 * Track entity view
 */
export const trackEntityView = (entityId, entityName) => {
  trackEvent('Entity', 'view', entityName, null, { entity_id: entityId })
  
  // Check for first entity view (activation milestone)
  trackFirstTimeAction('entity_view', 'Entity', 'first_entity_view', entityName, { entity_id: entityId })
}

/**
 * Track sighting created
 */
export const trackSightingCreated = (entityId) => {
  trackEvent('Sighting', 'created', null, null, { entity_id: entityId })
  
  // Check for first sighting (activation milestone)
  trackFirstTimeAction('sighting_created', 'Sighting', 'first_sighting', null, { entity_id: entityId })
}

/**
 * Track sighting confirmation
 */
export const trackConfirmation = (sightingId) => {
  trackEvent('Sighting', 'confirmed', null, null, { sighting_id: sightingId })
  
  // Check for first confirmation (activation milestone)
  trackFirstTimeAction('confirmation', 'Sighting', 'first_confirmation', null, { sighting_id: sightingId })
}

/**
 * Track favorite toggle
 */
export const trackFavoriteToggle = (entityId, entityName, action, source = 'entity_detail') => {
  trackEvent('Entity', 'favorite_' + action, entityName, null, {
    entity_id: entityId,
    source: source
  })
  
  // Check for first favorite (activation milestone) - only on 'add' action
  if (action === 'add') {
    trackFirstTimeAction('favorite', 'Entity', 'first_favorite', entityName, { entity_id: entityId, source })
  }
}

/**
 * Track share action
 */
export const trackEntityShare = (entityId, entityName, platform, source = 'entity_detail') => {
  trackEvent('Entity', 'share', platform || 'native', null, {
    entity_id: entityId,
    entity_name: entityName,
    share_platform: platform || 'native_share',
    source: source
  })
}

/**
 * Track review submission
 */
export const trackReviewSubmission = (entityId, rating, hasComment) => {
  trackEvent('Entity', 'review_submitted', null, rating, {
    entity_id: entityId,
    has_comment: hasComment
  })
}

/**
 * Track gallery image click
 */
export const trackGalleryImageClick = (entityId, imageId, isPrimary, imageIndex) => {
  trackEvent('Entity', 'gallery_image_click', entityId, imageIndex, {
    entity_id: entityId,
    image_id: imageId,
    is_primary: isPrimary,
    image_position: imageIndex
  })
}

/**
 * Track gallery load more
 */
export const trackGalleryLoadMore = (entityId, totalImages, loadedImages) => {
  trackEvent('Entity', 'gallery_load_more', entityId, null, {
    entity_id: entityId,
    total_images: totalImages,
    loaded_images: loadedImages
  })
}

/**
 * Track navigation to entity edit
 */
export const trackEntityEditNavigation = (entityId, entityName, source = 'entity_detail') => {
  trackEvent('Entity', 'navigate_to_edit', entityName, null, {
    entity_id: entityId,
    source: source
  })
}

/**
 * Track get directions click
 */
export const trackGetDirections = (entityId, entityName, hasUserLocation, source = 'entity_detail') => {
  trackEvent('Entity', 'get_directions', entityName, null, {
    entity_id: entityId,
    has_user_location: hasUserLocation,
    source: source
  })
}

/**
 * Track view on Google Maps click
 */
export const trackViewOnGoogleMaps = (entityId, entityName, source = 'entity_detail') => {
  trackEvent('Entity', 'view_on_google_maps', entityName, null, {
    entity_id: entityId,
    source: source
  })
}

/**
 * Track entity card impression (visible in list)
 */
export const trackEntityImpression = (entityId, entityName, position, source = 'search_results') => {
  trackEvent('Entity', 'impression', entityId, position, {
    entity_id: entityId,
    entity_name: entityName,
    list_position: position,
    source: source
  })
}

/**
 * Track entity card click
 */
export const trackEntityCardClick = (entityId, entityName, position, source = 'search_results') => {
  trackEvent('Entity', 'card_click', entityId, position, {
    entity_id: entityId,
    entity_name: entityName,
    list_position: position,
    source: source
  })
}

/**
 * Track tab changes in entity detail
 */
export const trackEntityTabChange = (entityId, fromTab, toTab, timeOnTab) => {
  trackEvent('Entity', 'tab_change', entityId, null, {
    entity_id: entityId,
    from_tab: fromTab,
    to_tab: toTab,
    time_on_previous_tab_seconds: timeOnTab
  })
}

/**
 * Track sighting confirmation with image
 */
export const trackSightingConfirm = (sightingId, entityId, hasImage, hasNotes, confirmationCount) => {
  trackEvent('Sighting', 'confirm', sightingId, confirmationCount, {
    sighting_id: sightingId,
    entity_id: entityId,
    has_image: hasImage,
    has_notes: hasNotes,
    confirmation_count: confirmationCount
  })
}

/**
 * Track sighting image upload
 */
export const trackSightingImageUpload = (sightingId, imageCount, totalSizeKb) => {
  trackEvent('Sighting', 'image_upload', sightingId, imageCount, {
    sighting_id: sightingId,
    image_count: imageCount,
    total_size_kb: totalSizeKb
  })
}
